import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Brand";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#090A0C] text-slate-50 evo-blueprint-grid">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-12">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400" data-testid="login-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="rounded-2xl border border-slate-800 bg-[#151820] p-7 shadow-2xl">
          <Logo />
          <h1 className="mt-6 font-heading text-2xl font-bold">Sign in</h1>
          <p className="mt-1 text-sm text-slate-400">Access your quotations and project portal.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-300">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-4 py-3 text-slate-100 outline-none focus:border-amber-500"
                data-testid="login-email-input" placeholder="you@example.com" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-300">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-4 py-3 text-slate-100 outline-none focus:border-amber-500"
                data-testid="login-password-input" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading}
              className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 px-6 py-3.5 font-bold text-slate-950 transition-all hover:from-amber-500 hover:to-amber-400 active:scale-95 disabled:opacity-60"
              data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="mt-5 text-center text-sm text-slate-400">
            <Link to="/forgot-password" className="font-semibold text-amber-400 hover:underline" data-testid="login-forgot-link">
              Forgot your password?
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-slate-400">
            New to EvoStruct?{" "}
            <Link to="/" className="font-semibold text-amber-400 hover:underline" data-testid="login-start-quote-link">
              Start a quote
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
